import Meta from '@src/components/Meta';
import BloodHomeCard from '@src/components/Organism/Card/BloodHomeCard';
import BloodHomeListHeader from '@src/components/Organism/Card/BloodHomeListHeader';
import PageTemplate from '@src/components/Template/Page';
import { metaInfo } from '@src/data/meta';
import useBlood from '@src/hooks/useBlood';
import { IBloodHome } from '@src/interfaces/blood';
import { BloodKeys } from '@src/keys/bloodKey';
import keywordAtom from '@src/recoils/atom/keyword';
import { blood } from '@src/services/api';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { useMemo, useState } from 'react';
import { useRecoilState } from 'recoil';
import LoadingBloodHomeSkeleton from '@src/components/SkeletonBloodHome';
import styled from 'styled-components';
import { devices } from '@src/styles/theme';

const EmptyArray = new Array(8).fill(0);

export const getServerSideProps: GetServerSideProps = async () => {
  const queryClient = new QueryClient();
  await queryClient.prefetchQuery(
    [BloodKeys.home],
    async () => await blood.getBloodHome('')
  );
  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
};

/**
 * 헌혈의 집 목록 (디자인 시스템 적용).
 * 헌혈요청·커뮤니티 목록과 같은 단일 컬럼 구성 — 타이틀/검색/필터 칩 + 카드 리스트.
 * 혈액원 필터는 검색 API에 파라미터가 없어 클라이언트에서 거른다.
 */
const BloodHomePage = () => {
  const { bloodHomeReview } = metaInfo;
  const [keyword, setKeyword] = useRecoilState(keywordAtom);
  const [searchInput, setSearchInput] = useState(keyword ?? '');
  const [bank, setBank] = useState('');
  const { fetchBloodHomeList } = useBlood();
  const { data: bloodHomeList, isLoading } = fetchBloodHomeList(keyword);

  const homes = useMemo(() => {
    const all = (bloodHomeList ?? []) as IBloodHome[];
    return bank ? all.filter((h) => h.kind === bank) : all;
  }, [bloodHomeList, bank]);

  return (
    <>
      <Meta
        title={bloodHomeReview.title}
        description={bloodHomeReview.description}
        ogImage={bloodHomeReview.ogImage}
      />
      <PageTemplate
        hideAside
        maxWidth={1120}
        href={'/blood-home'}
        mainText={'헌혈의집'}
      >
        <Main>
          <BloodHomeListHeader
            totalCount={isLoading ? undefined : homes.length}
            keyword={searchInput}
            onKeywordChange={setSearchInput}
            onSearch={() => setKeyword(searchInput)}
            activeBank={bank}
            onBankChange={setBank}
          />
          {isLoading &&
            EmptyArray.map((e, i) => <LoadingBloodHomeSkeleton key={i} />)}
          {!isLoading &&
            homes.map((home) => (
              <BloodHomeCard key={home.uuid} home={home} />
            ))}
          {!isLoading && homes.length === 0 && (
            <Empty>검색된 헌혈센터가 없습니다.</Empty>
          )}
        </Main>
      </PageTemplate>
    </>
  );
};

const Main = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Empty = styled.div`
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 20px;
  padding: 60px 20px;
  text-align: center;
  font-size: 14px;
  color: #767676;
  @media ${devices.tablet} {
    border-radius: 16px;
  }
`;

export default BloodHomePage;
